"use client";

import { faCalendar, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CalendarDateTime,
  endOfMonth,
  endOfWeek,
  getLocalTimeZone,
  startOfMonth,
  startOfWeek,
  today,
} from "@internationalized/date";
import { useControlledState } from "@react-stately/utils";
import { useCallback, useMemo, useState } from "react";
import { useDateFormatter } from "react-aria";
import { DateRangePicker as AriaDateRangePicker, useLocale } from "react-aria-components";
import { tv } from "tailwind-variants";

import { cn } from "../../utils/cn";
import { Button as RivetButton } from "../button/Button";
import { Popover } from "../popover/Popover";
import { Text } from "../typography/Text";
import { DateInput } from "./DateInput";
import { RangeCalendar } from "./RangeCalendar";
import { RangePresetButton } from "./RangePreset";

import type {
  DateRangePickerProps as AriaDateRangePickerProps,
  DateValue,
} from "react-aria-components";

const dateRangePickerVariants = tv({
  slots: {
    trigger: [
      "flex w-full justify-between gap-3 rounded border border-solid text-left",
      "cursor-pointer items-center bg-field px-3 py-1 transition-colors",
      "dark:border-gray-500 dark:bg-gray-850",
    ],
    triggerText: "min-w-0 truncate",
    triggerIcon: "ml-2 shrink-0 text-secondary dark:text-gray-400",
    popover: [
      "rounded-md border border-gray-200 bg-white shadow-xl",
      "!p-0 dark:border-gray-500 dark:bg-gray-850",
    ],
    calendar: "flex flex-col gap-3 p-3",
    header: "flex w-full items-center gap-1 px-2",
  },
  variants: {
    isOpen: {
      true: {
        trigger: [
          "border-field-selected ring-2 ring-blue-100",
          "dark:border-blue-600 dark:ring-blue-700",
        ],
      },
      false: {
        trigger: [
          "border-field hover:border-gray-300 hover:bg-gray-20",
          "dark:hover:border-gray-400 dark:hover:bg-gray-800",
        ],
      },
    },
    hasValue: {
      true: {
        triggerText: "text-primary dark:text-gray-100",
      },
      false: {
        triggerText: "text-secondary dark:text-gray-400",
      },
    },
  },
});

const now = today(getLocalTimeZone());

const highlightedDates = [today(getLocalTimeZone())];

export interface DateRangePickerProps
  extends AriaDateRangePickerProps<DateValue>,
    Omit<AriaDateRangePickerProps<DateValue>, "value" | "onChange" | "className"> {
  /** The value held by the DateRangePicker. */
  value?: { start: DateValue; end: DateValue } | null;
  /** When true, shows Save/Cancel footer and only applies on Save. */
  showSaveCancelButtons?: boolean;
  /** Optional class name applied to the root of the DateRangePicker. */
  className?: string;
  /** The callback function called when the value changes. */
  onChange?: (value: { start: DateValue; end: DateValue } | null) => void;
  /** The function to call when the apply button is clicked. */
  onApply?: () => void;
  /** The function to call when the cancel button is clicked. */
  onCancel?: () => void;
  /** Whether to include time in the date range picker. */
  includeTime?: boolean;
  /** Function to format the date and time for the trigger text. */
  formatDateTime?: (dateObject: Date, includeTime?: boolean) => string;
  /** Custom preset ranges to display. If provided, these will be used instead of default presets. */
  ranges?: Record<string, { start: DateValue; end: DateValue }>;
  /** Whether to show the clear button. Defaults to true. */
  allowClear?: boolean;
  /** The container element in which the popover portal will be placed. Useful for fullscreen mode. */
  portalContainer?: Element;
}

export const DateRangePicker = ({
  value: valueProp,
  defaultValue,
  onChange,
  onApply,
  onCancel,
  showSaveCancelButtons = false,
  className,
  includeTime,
  formatDateTime,
  ranges: customRanges,
  allowClear = true,
  portalContainer,
  ...props
}: DateRangePickerProps) => {
  const slots = dateRangePickerVariants();
  const { locale } = useLocale();
  const formatter = useDateFormatter({
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timeFormatter = useDateFormatter({
    hour: "numeric",
    minute: "numeric",
  });
  const [value, setValue] = useControlledState(valueProp, defaultValue || null, onChange);
  const [focusedValue, setFocusedValue] = useState<DateValue | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Normalize range to ensure start <= end
  // When includeTime is true, preserve the previous start/end time if available,
  // otherwise fall back to day bounds (start: 00:00, end: 23:59).
  const handleRangeChange = useCallback(
    (next: { start: DateValue; end: DateValue } | null) => {
      if (!next) {
        setValue(null);
        return;
      }

      let { start, end } = next;

      if (start && end) {
        if (start.compare(end) > 0) {
          const tmp = start;
          start = end;
          end = tmp;
        }
      }

      if (includeTime && start && end) {
        const getTimeFor = (
          current: DateValue,
          prev: DateValue | null | undefined,
          isEnd: boolean
        ) => {
          // Defaults when there is no time information anywhere.
          let hour = isEnd ? 23 : 0;
          let minute = isEnd ? 59 : 0;

          // Prefer the time from the new incoming value if present.
          if ("hour" in current && typeof current.hour === "number") {
            hour = current.hour;
            minute = current.minute;
            return { hour, minute };
          }

          // Otherwise, fall back to the previous stored value's time if present.
          if (prev && "hour" in prev && typeof prev.hour === "number") {
            hour = prev.hour;
            minute = prev.minute;
          }

          return { hour, minute };
        };

        const prevStart = value?.start;
        const prevEnd = value?.end;

        const startTime = getTimeFor(start, prevStart, false);
        const endTime = getTimeFor(end, prevEnd, true);

        setValue({
          start: new CalendarDateTime(
            start.year,
            start.month,
            start.day,
            startTime.hour,
            startTime.minute,
            0,
            0
          ),
          end: new CalendarDateTime(
            end.year,
            end.month,
            end.day,
            endTime.hour,
            endTime.minute,
            0,
            0
          ),
        });
        return;
      }

      setValue({ start, end });
    },
    [setValue, includeTime, value]
  );

  const formatDateTimeFn = (date: Date) => {
    if (formatDateTime) {
      return formatDateTime(date, includeTime);
    }

    if (includeTime) {
      return `${formatter.format(date)} ${timeFormatter.format(date)}`;
    }

    return formatter.format(date);
  };

  const formatPart = (dateValue: DateValue | null | undefined) => {
    if (!dateValue) {
      return "Select date";
    }
    const jsDate = dateValue.toDate(getLocalTimeZone());
    return formatDateTimeFn(jsDate);
  };

  const formattedStartDate = formatPart(value?.start);
  const formattedEndDate = formatPart(value?.end);

  const presets = useMemo(() => {
    // If custom ranges are provided, convert them to preset format
    if (customRanges) {
      return Object.entries(customRanges).reduce(
        (acc, [key, value]) => {
          acc[key] = {
            label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, " $1"),
            value,
          };
          return acc;
        },
        {} as Record<string, { label: string; value: { start: DateValue; end: DateValue } }>
      );
    }

    // Otherwise, use default presets
    const toDateTimeIfNeeded = (date: DateValue, isEnd: boolean) => {
      if (!includeTime) {
        return date;
      }

      if (isEnd) {
        // End of day: 23:59:00.000 -- we are not allowing editing of sec and millisec
        return new CalendarDateTime(date.year, date.month, date.day, 23, 59, 0, 0);
      }

      // Start of day: 00:00:00.000
      return new CalendarDateTime(date.year, date.month, date.day, 0, 0, 0, 0);
    };

    const todayDate = now;
    const yesterdayDate = now.subtract({ days: 1 });
    const thisWeekStart = startOfWeek(now, locale);
    const thisWeekEnd = endOfWeek(now, locale);
    const lastWeekStart = thisWeekStart.subtract({ weeks: 1 });
    const lastWeekEnd = thisWeekEnd.subtract({ weeks: 1 });
    const thisMonthStart = startOfMonth(now);
    const thisMonthEnd = endOfMonth(now);
    const lastMonthStart = thisMonthStart.subtract({ months: 1 });
    const lastMonthEnd = thisMonthEnd.subtract({ months: 1 });
    const thisYearStart = startOfMonth(now.set({ month: 1 }));
    const thisYearEnd = endOfMonth(now.set({ month: 12 }));
    const lastYearStart = startOfMonth(now.set({ month: 1 }).subtract({ years: 1 }));
    const lastYearEnd = endOfMonth(now.set({ month: 12 }).subtract({ years: 1 }));
    const allTimeStart = now.set({ year: 2000, month: 1, day: 1 });
    const allTimeEnd = now;

    return {
      today: {
        label: "Today",
        value: {
          start: toDateTimeIfNeeded(todayDate, false),
          end: toDateTimeIfNeeded(todayDate, true),
        },
      },
      yesterday: {
        label: "Yesterday",
        value: {
          start: toDateTimeIfNeeded(yesterdayDate, false),
          end: toDateTimeIfNeeded(yesterdayDate, true),
        },
      },
      thisWeek: {
        label: "This week",
        value: {
          start: toDateTimeIfNeeded(thisWeekStart, false),
          end: toDateTimeIfNeeded(thisWeekEnd, true),
        },
      },
      lastWeek: {
        label: "Last week",
        value: {
          start: toDateTimeIfNeeded(lastWeekStart, false),
          end: toDateTimeIfNeeded(lastWeekEnd, true),
        },
      },
      thisMonth: {
        label: "This month",
        value: {
          start: toDateTimeIfNeeded(thisMonthStart, false),
          end: toDateTimeIfNeeded(thisMonthEnd, true),
        },
      },
      lastMonth: {
        label: "Last month",
        value: {
          start: toDateTimeIfNeeded(lastMonthStart, false),
          end: toDateTimeIfNeeded(lastMonthEnd, true),
        },
      },
      thisYear: {
        label: "This year",
        value: {
          start: toDateTimeIfNeeded(thisYearStart, false),
          end: toDateTimeIfNeeded(thisYearEnd, true),
        },
      },
      lastYear: {
        label: "Last year",
        value: {
          start: toDateTimeIfNeeded(lastYearStart, false),
          end: toDateTimeIfNeeded(lastYearEnd, true),
        },
      },
      allTime: {
        label: "All time",
        value: {
          start: toDateTimeIfNeeded(allTimeStart, false),
          end: toDateTimeIfNeeded(allTimeEnd, true),
        },
      },
    };
  }, [locale, includeTime, customRanges]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    setIsOpen(false);
  }, [onCancel]);

  const handleApply = useCallback(() => {
    onApply?.();
    setIsOpen(false);
  }, [onApply]);

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setValue(null);
    },
    [setValue]
  );

  const handleClearMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleClearMouseOver = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handlePresetClick = useCallback(
    (preset: { start: DateValue; end: DateValue }) => {
      handleRangeChange(preset);
      setFocusedValue(preset.start);
    },
    [handleRangeChange]
  );

  const presetButtons = useMemo(
    () =>
      Object.values(presets).map(preset => (
        <RangePresetButton
          key={preset.label}
          value={preset.value}
          onClick={() => handlePresetClick(preset.value)}
        >
          {preset.label}
        </RangePresetButton>
      )),
    [presets, handlePresetClick]
  );

  const rangeCalendarPresets = useMemo(
    () => ({
      lastWeek: presets.lastWeek,
      lastMonth: presets.lastMonth,
      lastYear: presets.lastYear,
    }),
    [presets]
  );

  return (
    <AriaDateRangePicker
      aria-label="Date range picker"
      value={value}
      className={className}
      onChange={handleRangeChange}
      // For including time, set to minute not seconds as the ANTD time picker was picking 15 mins intervals
      granularity={includeTime ? "minute" : "day"}
      {...props}
    >
      <Popover
        placement="bottom-start"
        onOpenChange={setIsOpen}
        isOpen={isOpen}
        triggerScaleOnOpen={false}
        isKeyboardDismissDisabled={showSaveCancelButtons}
        shouldCloseOnInteractOutside={() => !showSaveCancelButtons}
        portalContainer={portalContainer}
      >
        <Popover.Trigger>
          <RivetButton
            fullWidth
            aria-label="Open calendar"
            className={slots.trigger({ isOpen })}
            isDisabled={props.isDisabled}
          >
            {value ? (
              <span className="flex min-w-0 flex-1 items-center gap-x-2">
                <Text className={slots.triggerText({ hasValue: true })}>{formattedStartDate}</Text>
                <Text className="shrink-0">→</Text>
                <Text className={slots.triggerText({ hasValue: true })}>{formattedEndDate}</Text>
              </span>
            ) : (
              <Text className={cn(slots.triggerText({ hasValue: false }), "text-placeholder")}>
                Select dates
              </Text>
            )}
            {!value || !allowClear ? (
              <FontAwesomeIcon icon={faCalendar} className={slots.triggerIcon()} />
            ) : (
              <RivetButton
                variant="light"
                size="xs"
                className="bg-transparent p-0 hover:bg-transparent hover:text-secondary dark:bg-transparent dark:hover:bg-transparent dark:hover:text-gray-400"
                onMouseOver={handleClearMouseOver}
                aria-label="Clear dates"
                onClick={handleClearClick}
                onMouseDown={handleClearMouseDown}
              >
                <FontAwesomeIcon icon={faXmark} />
              </RivetButton>
            )}
          </RivetButton>
        </Popover.Trigger>
        <Popover.Content className={slots.popover()}>
          <div className="flex">
            <div className="w-38 hidden flex-col gap-0.5 border border-y-0 border-l-0 border-solid border-gray-200 p-3 dark:border-gray-700 lg:flex">
              {presetButtons}
            </div>
            <div className="flex h-full flex-col">
              <RangeCalendar
                focusedValue={focusedValue}
                onFocusChange={setFocusedValue}
                highlightedDates={highlightedDates}
                presets={rangeCalendarPresets}
              />
              <div className="flex items-center gap-3 border border-x-0 border-b-0 border-solid border-gray-200 p-3 dark:border-gray-700 ">
                <div className="hidden items-center gap-3 md:flex">
                  <DateInput slot="start" className={includeTime ? "w-56" : "w-36"} />
                  <Text>→</Text>
                  <DateInput slot="end" className={includeTime ? "w-56" : "w-36"} />
                </div>
                {showSaveCancelButtons && (
                  <div className="ml-auto flex items-center gap-2">
                    <RivetButton onClick={handleCancel}>Cancel</RivetButton>
                    <RivetButton variant="primary" onClick={handleApply}>
                      Save
                    </RivetButton>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover>
    </AriaDateRangePicker>
  );
};
